import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client'

import { useLoading, useServerError } from 'shared/hooks'
import { GET_COUNTRY_LIST, GET_LABEL_BY_ID } from 'modules/Labels/graphql/queries'
import {
  useCreateLabelTemplate,
  useCreateUnit,
  useUpdateLabel,
  useUpdateLabelTemplate,
  useUpdateUnit
} from 'modules/Labels/graphql/mutations'

import { currentUserVar, notificationMessageVar } from 'modulesCommon/store'
import { countryCodeListVar, currentLabelVar } from 'modules/Labels/store'

import { sortById } from 'shared/utils/sortById'

import { IUnit } from 'shared/interfaces'
import { TOption } from 'shared/types/SelectOptions'
import { GetLabelByIdQuery, GetLabelByIdQueryVariables } from 'modules/Labels/graphql/queries/getLabelById.types'

export const useServiceUpdateLabel = (id: string = '') => {
  const currentUser = useReactiveVar(currentUserVar)
  const countryCodesList = useReactiveVar(countryCodeListVar)
  const currentLabel = useReactiveVar(currentLabelVar)

  const navigate = useNavigate()

  const [getLabelById, { loading: isLabelLoading, error: labelError }] = useLazyQuery<GetLabelByIdQuery, GetLabelByIdQueryVariables>(GET_LABEL_BY_ID, {
    variables: { id },
    fetchPolicy:'cache-and-network',
    onCompleted: (data) => currentLabelVar(data.getLabelById || null)
  })

  const { loading: isCountryCodesListLoading, error: countryCodesError } = useQuery(GET_COUNTRY_LIST, {
    onCompleted: (data) => countryCodeListVar(data.getCountryList || [])
  })
  const { mutate: updateLabel, loading: isLoading, error: updateLabelError } = useUpdateLabel()
  const { mutate: createUnit, loading: isUnitLoading, error: createUnitError } = useCreateUnit()
  const { mutate: updateUnit, loading: isUpdateLoading, error: updateUnitError } = useUpdateUnit()
  const { mutate: createTemplate, loading: isCreateTemplateLoading } = useCreateLabelTemplate()
  const { mutate: updateTemplate, loading: isUpdateTemplateLoading } = useUpdateLabelTemplate()

  const countryOptions = useMemo(
    (): TOption[] =>
      countryCodesList.map((item) => ({
        key: item.code,
        title: `${item.code} - ${item.name}`
      })),
    [countryCodesList]
  )

  const onSubmitLabel = useCallback(
    (values) => {
      updateLabel({
        variables: { input: values }
      }).then(() => {
        navigate('/admin/labels')
        notificationMessageVar({
          message: 'Zone has been successfully updated',
          title: 'Success',
          type: 'success'
        })
      })
    },
    [navigate, updateLabel]
  )

  const defaultLabelValues = useMemo(
    () => ({
      alias: currentLabel?.alias || '',
      name: currentLabel?.name || '',
      id: currentLabel?.id,
      country_codes: currentLabel?.country_codes || [],
      ...(currentLabel?.max_radio_stations && { max_radio_stations: currentLabel?.max_radio_stations })
    }),
    [currentLabel]
  )

  const onUpdateTemplate = useCallback(
    (id: string, template: string) => updateTemplate({ variables: { id, template } }),
    [updateTemplate]
  )

  const onCreateTemplate = useCallback((input) => createTemplate({ variables: { input } }), [createTemplate])

  const onGoBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const units = useMemo(() => [...(currentLabel?.units || [])].sort(sortById), [currentLabel?.units])

  const isLocalEditable = useMemo(
    () =>
      currentUser?.label_id === id ||
      currentUser?.role?.role_id === 'admin' ||
      currentUser?.role?.role_id === 'global-manager',
    [currentUser, id]
  )

  const onCreateUnit = useCallback(
    async (values: IUnit) => {
      await createUnit({ variables: { input: { ...values, label_id: id } } })
      await getLabelById()
    },
    [createUnit, getLabelById, id]
  )

  const onUpdateUnit = useCallback(
    async (values: IUnit) => {
      await updateUnit({ variables: { input: { ...values, label_id: id } } })
      await getLabelById()
    },
    [updateUnit, getLabelById, id]
  )

  useEffect(() => {
    if (id) {
      getLabelById()
    }
  }, [getLabelById, id])

  useLoading({
    isLoading:
      isLoading ||
      isLabelLoading ||
      isCountryCodesListLoading ||
      isUnitLoading ||
      isUpdateLoading
  })

  useServerError([
    labelError,
    updateLabelError,
    countryCodesError,
    createUnitError,
    updateUnitError
  ])

  return {
    currentUser,
    units,
    currentLabel,
    defaultLabelValues,
    onCreateUnit,
    onUpdateUnit,
    onGoBack,
    onCreateTemplate,
    onUpdateTemplate,
    isUpdateTemplateLoading,
    isCreateTemplateLoading,
    countryOptions,
    onSubmitLabel,
    isUpdateLoading,
    isLocalEditable,
    notificationMessageVar
  }
}
